// Nối SaveStore với các SaveSlice: nạp lúc boot, autosave debounce 800ms, lưu ngay khi trang ẩn
// (visibilitychange → hidden, pagehide cho iOS, beforeunload) (TECH_BASELINE §7.2).
import { effect, signal, computed } from '@preact/signals';
import type { LoadResult, SaveStore } from '@save/store';
import type { SaveDoc } from '@save/types';
import { collectDoc, hydrateAll } from './registry';
import { now } from './clock';
import { logLine } from './modules/log';

export const AUTOSAVE_DEBOUNCE_MS = 800;

let store: SaveStore | null = null;
let base: SaveDoc | null = null;
let timer: ReturnType<typeof setTimeout> | null = null;
let saving: Promise<void> = Promise.resolve();
let disposeWatch: (() => void) | null = null;
let mirrorWarned = false;

const _readOnly = signal(false);
export const saveReadOnly = computed(() => _readOnly.value);

function watchSlices(): void {
  disposeWatch?.();
  let first = true;
  disposeWatch = effect(() => {
    // Đọc serialize() của mọi slice để effect theo dõi mọi signal được lưu.
    if (base) collectDoc(base, 0);
    if (first) {
      first = false;
      return;
    }
    markDirty();
  });
}

export async function bootSave(s: SaveStore): Promise<LoadResult> {
  store = s;
  const res = await s.load();
  base = res.doc;
  _readOnly.value = res.readOnly;
  hydrateAll(res.doc);
  if (res.quarantined.length > 0) logLine('system', 'log.system.saveQuarantined');
  if (res.migratedFrom !== undefined) logLine('system', 'log.system.saveMigrated');
  if (res.source === 'fresh') logLine('system', 'log.system.welcome');
  watchSlices();
  return res;
}

export function markDirty(): void {
  if (timer !== null) clearTimeout(timer);
  timer = setTimeout(() => void flush(), AUTOSAVE_DEBOUNCE_MS);
}

/** Lưu ngay; lỗi không bao giờ chặn UI, chỉ ghi Truyền Âm kênh Hệ Thống. */
export function flush(): Promise<void> {
  if (timer !== null) clearTimeout(timer);
  timer = null;
  saving = saving.then(async () => {
    if (!store || !base) return;
    const doc = collectDoc(base, now());
    const rep = await store.save(doc);
    base = doc;
    if (rep.idb === 'failed' && rep.mirror !== 'ok') logLine('system', 'log.system.saveFailed');
    if (rep.mirror === 'tooLarge' && !mirrorWarned) {
      mirrorWarned = true;
      logLine('system', 'log.system.saveMirrorSkipped');
    }
  });
  return saving;
}

/** Thay thân hiện tại bằng doc đã nhập (đã kiểm + di trú). Bản cũ vào backup trước khi đè. */
export async function adoptDoc(doc: SaveDoc): Promise<void> {
  base = doc;
  hydrateAll(doc);
  logLine('system', 'log.system.saveImported');
  await flush();
}

/** Seed gốc của thế giới (uint32) trong save: nguồn seed cho sim (TECH_BASELINE §4.1). */
export function saveSeed(): number {
  return base?.seed ?? 0;
}

export function currentStore(): SaveStore | null {
  return store;
}

export function bindAutosave(target: Window = window): void {
  const doc = target.document;
  doc.addEventListener('visibilitychange', () => {
    if (doc.visibilityState === 'hidden') void flush();
  });
  target.addEventListener('pagehide', () => void flush());
  target.addEventListener('beforeunload', () => void flush());
}
